import { FullWebConfig } from "types/config";
import { AssetListStore } from "./AssetListStore";
import { DataTableStore } from "./DataTableStore";
import { LayoutStore } from "./LayoutStore";
import { UserRolePermissionStore } from "./UserRolePermissionStore";
import { RolesListStore } from "./config/RoleListStore";
import { RoleStore } from "./config/RoleStore";
import { SystemAdministratorsStore } from "./config/SystemAdministratorsStore";
import { UsersStore } from "./config/UsersStore";
import { WorkgroupsStore } from "./config/WorkgroupsStore";
import { action, computed, makeObservable, observable } from "mobx";
import { getVenueList } from "api/venue.api";
import { Venue } from "types/venue";
import { UserInfo } from "types/userInfo";
import { AssetStore } from "./AssetStore";
import { UserListStore } from "./UserListStore";
import { BeaconListStore } from "./BeaconListStore";
import { keys } from "lodash";
import { UnregisteredBeaconStore } from "./UnregisteredBeaconStore";
import { getUserInfo, getWebConfig } from "api/app.api";
import { MapStore } from "./MapStore";
import { BeaconStore } from "./BeaconStore";
import { ErrorResponseDto } from "api/config/ErrorResponseDto";
import { DeviceListStore } from "./DeviceListStore";

export class RootStore {
  layoutStore!: LayoutStore;
  workgroupsStore!: WorkgroupsStore;
  rolesListStore!: RolesListStore;
  roleStore!: RoleStore;
  systemAdministratorsStore!: SystemAdministratorsStore;
  dataTableStore!: DataTableStore;
  userStore!: UsersStore;
  userRolePermissionStore!: UserRolePermissionStore;
  assetListStore!: AssetListStore;
  deviceListStore!: DeviceListStore;
  assetStore!: AssetStore;
  userListStore!: UserListStore;
  beaconlistStore!: BeaconListStore;
  beaconStore!: BeaconStore;
  unregisteredBeaconStore!: UnregisteredBeaconStore;
  mapStore!: MapStore;

  static authToken: string;
  static accessToken: string;

  webConfig!: FullWebConfig;

  // multiple venues is not requested and implemented. So we only assume a single venue
  venue !: Venue;
  userInfo!: UserInfo;

  constructor() {

    makeObservable(this, {
      webConfig: observable,
      venue: observable,
      userInfo: observable,

      loadAppData: action.bound,
      getUncertaintyValue: action.bound,

      beaconIntegration: computed,
      hasBeaconIntegration: computed,
      freshPeriodThresholdSeconds: computed,
      version: computed,
      mapPollingPeriodSeconds: computed,
      availableFeatures: computed,
      getUnit: computed,
      hasDevicesFeature: computed
    });
  }

  loadAppData() {
    return Promise.all([
      getWebConfig(),
      getUserInfo(RootStore.accessToken),
    ]).then(async res => {

      this.webConfig = res[0]
      this.userInfo = res[1]
      try {
        const response = await getVenueList(res[0].services.venue_service)
        if (response.venues.length === 0)
          throw new ErrorResponseDto("Can not load Venue Data")
        this.venue = response.venues[0]
      }
      catch {
        throw new ErrorResponseDto("Can not load Venue Data")
      }

      this.userRolePermissionStore = new UserRolePermissionStore(this);
      try {
        await this.userRolePermissionStore.loadPermissions()
      }
      catch {
        throw new ErrorResponseDto("An unexpected error on loading data.")
      }
      this.layoutStore = new LayoutStore(this);
      this.workgroupsStore = new WorkgroupsStore(this);
      this.rolesListStore = new RolesListStore(this);
      this.roleStore = new RoleStore(this);
      this.systemAdministratorsStore = new SystemAdministratorsStore(this);
      this.userStore = new UsersStore(this);
      this.dataTableStore = new DataTableStore(this);
      this.assetListStore = new AssetListStore(this);
      this.deviceListStore = new DeviceListStore(this);
      this.assetStore = new AssetStore(this);
      this.userListStore = new UserListStore(this);
      this.beaconlistStore = new BeaconListStore(this);
      this.beaconStore = new BeaconStore(this);
      this.unregisteredBeaconStore = new UnregisteredBeaconStore(this);
      this.mapStore = new MapStore(this);

    })
  }

  getUncertaintyValue(uncertainty: number | undefined | null): string {
    if (uncertainty === undefined || uncertainty === null)
      return "––"

    let value = uncertainty

    if (this.webConfig.units === "imperial")
      value = uncertainty * 3.28084

    return value.toFixed(1) + " " + this.getUnit
  }

  get getUnit() {
    if (!this.webConfig.units)
      return "metres"
    return this.webConfig.units === "metric" ? "metres" : "feet"
  }

  get beaconIntegration() {
    // get matching single integration that has a type of 'beacon_import', otherwise return null

    let matchingIntergration = '';

    keys(this.webConfig.featureset.integrations).forEach(integrationKey => {
      if (this.webConfig.featureset.integrations[integrationKey].type === 'beacon_import') {
        matchingIntergration = this.webConfig.featureset.integrations[integrationKey].integration.integration;
      }
    });

    return matchingIntergration;
  }

  get availableFeatures() {
    return this.webConfig?.features
  }

  get hasDevicesFeature() {
    return this.webConfig?.featureset.device_tracking
  }

  get version() {
    return this.webConfig?.version.version
  }

  get mapPollingPeriodSeconds() {
    return this.webConfig.polling_period ? this.webConfig.polling_period : 10
  }

  get hasBeaconIntegration() {
    return this.beaconIntegration !== '';
  }

  get freshPeriodThresholdSeconds() {
    return this.webConfig.ui.map_element.color_policy.fresh_period_threshold;
  }

}
